import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react'
import { toast } from 'react-toastify'
import apiService from '../../services/api'
import SEO from '../../components/SEO'
import { withCaptcha } from '../../modules/captcha'
import { useLocalization } from '../../hooks/useLocalization'

interface ForgotPasswordForm {
  email: string
}

function ForgotPasswordPage() {
  const { t } = useLocalization()
  const [form, setForm] = useState<ForgotPasswordForm>({
    email: ''
  })
  const [loading, setLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await apiService.authPost('/auth/forgot-password', form)
      setIsSuccess(true)
      toast.success('Password reset instructions sent to your email')
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to send reset instructions'
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO 
        title="Forgot Password"
        description="Reset your Evoltbay account password"
        url={window.location.href}
        noIndex={true}
      />

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link 
          to="/login"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t('auth.forgotPassword.backToLogin')}
        </Link>

        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t('auth.forgotPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isSuccess ? (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('auth.forgotPassword.checkEmail')}</h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('auth.forgotPassword.sentInstructions')} {form.email}
              </p>
              <div className="mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {t('auth.forgotPassword.returnToLogin')}
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('auth.forgotPassword.emailLabel')}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetInstructions')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default withCaptcha(ForgotPasswordPage) 